// Locale registry. To enable a locale, add its catalog to ./messages/<code>.json.
// A locale is "supported" (routable, offered in the switcher) only when its
// catalog exists; English is always the default and fallback.

export const DEFAULT_LOCALE = "en";

// All launch locales from the product plan (architecture §15.2).
// `fontFamily` names an extra web font loaded only while that locale is active.
export const LOCALE_DEFS = [
  { code: "en", dir: "ltr" },
  { code: "es", dir: "ltr" },
  { code: "zh-CN", dir: "ltr", fontFamily: "Noto+Sans+SC:wght@400;500;600;700" },
  { code: "fr", dir: "ltr" },
  { code: "de", dir: "ltr" },
];

// Vite resolves this at build time; each catalog becomes its own lazy chunk,
// except English which is bundled eagerly as the fallback.
const lazyCatalogs = import.meta.glob(["./messages/*.json", "!./messages/en.json"], {
  import: "default",
});

export const CATALOG_CODES = new Set([
  DEFAULT_LOCALE,
  ...Object.keys(lazyCatalogs).map((p) => p.replace(/^.*\/(.+)\.json$/, "$1")),
]);

export const SUPPORTED_LOCALES = LOCALE_DEFS.filter((l) => CATALOG_CODES.has(l.code));
export const SUPPORTED_CODES = SUPPORTED_LOCALES.map((l) => l.code);

export function getLocaleDef(code) {
  return LOCALE_DEFS.find((l) => l.code === code) || LOCALE_DEFS[0];
}

export function isSupported(code) {
  return SUPPORTED_CODES.includes(code);
}

export function loadCatalog(code) {
  const loader = lazyCatalogs[`./messages/${code}.json`];
  return loader ? loader() : Promise.resolve(null);
}

/**
 * Pick the best supported locale for a list of BCP-47 tags (e.g. navigator.languages).
 * Exact match first ("zh-CN"), then language-only match ("de-AT" -> "de",
 * "zh" -> "zh-CN"), else the default.
 */
export function negotiateLocale(requested, supported = SUPPORTED_CODES) {
  const list = (requested || []).filter(Boolean);
  for (const tag of list) {
    const exact = supported.find((s) => s.toLowerCase() === tag.toLowerCase());
    if (exact) return exact;
  }
  for (const tag of list) {
    const lang = tag.split("-")[0].toLowerCase();
    const partial = supported.find((s) => s.split("-")[0].toLowerCase() === lang);
    if (partial) return partial;
  }
  return DEFAULT_LOCALE;
}

/** Native display name of a locale ("Deutsch", "English"), via Intl. */
export function localeDisplayName(code) {
  try {
    const name = new Intl.DisplayNames([code], { type: "language" }).of(code);
    return name ? name.charAt(0).toLocaleUpperCase(code) + name.slice(1) : code;
  } catch {
    return code;
  }
}
